import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { Warnings } from '../components/Warnings.js';

/**
 * หน้า "ผู้มีอำนาจสั่งบรรจุแต่งตั้ง"
 *
 * ตารางนี้ยังไม่สมบูรณ์โดยตั้งใจ ช่องที่ยังไม่มีเอกสารรองรับจะขึ้นว่า "ยังไม่ได้กรอก"
 * ไม่ใช่เดาให้ เพราะผู้มีอำนาจสั่งบรรจุกำหนดในมาตรา 57 แห่ง พ.ร.บ. ระเบียบข้าราชการพลเรือน
 * พ.ศ. 2551 การกรอกผิดแม้ช่องเดียวหมายถึงคำสั่งแต่งตั้งที่อาจไม่ชอบด้วยกฎหมาย
 */
export function Authority() {
  return (
    <section>
      <div className="wrap">
        <h2>ผู้มีอำนาจสั่งบรรจุและแต่งตั้ง</h2>
        <p className="sub">
          ผู้มีอำนาจสั่งบรรจุ ผู้ให้ความเห็นชอบ และขั้นตอนของแต่ละระดับตำแหน่ง
        </p>
        <AuthorityBody />
      </div>
    </section>
  );
}

/**
 * เนื้อในของหน้า — แยกออกมาเพื่อให้หน้าต่างบนผัง (components/AuthorityModal.tsx) ใช้ตัวเดียวกัน
 *
 * ⚠ ห้าม copy โครงนี้ไปวางซ้ำในไฟล์หน้าต่าง
 * ตารางนี้อ้างมาตรา 57 การมีสองสำเนาแปลว่าวันที่แก้ข้อความให้ตรงกฎหมาย
 * จะมีที่หนึ่งที่ถูกแก้ และอีกที่หนึ่งที่ยังบอกของเดิมอยู่ โดยไม่มีอะไรฟ้อง
 */
export function AuthorityBody() {
  const q = useQuery({ queryKey: ['authority'], queryFn: api.authority });

  if (q.isPending) return <p className="state">กำลังโหลด…</p>;
  if (q.isError) return <p className="state error">{String(q.error)}</p>;

  const filled = q.data.rows.filter((r) => r.appointedBy !== null).length;

  return (
    <>
        <div className="tally">
          <div>
            <span className="n">{filled}<span className="of">/{q.data.rows.length}</span></span>
            <span className="l">ระดับที่บันทึกข้อมูลครบถ้วนแล้ว</span>
          </div>
        </div>

        <Warnings items={q.data.warnings} />

        <div className="auth">
          {q.data.rows.map((r) => (
            <div className="acard" key={r.dpisLevel}>
              <div className="ahead">
                <b>{r.dpisLevel}</b>
                <span>{r.label}</span>
              </div>

              <div className="afields">
                <div>
                  <dt>ผู้มีอำนาจสั่งบรรจุและแต่งตั้ง</dt>
                  <dd className={r.appointedBy ? '' : 'todo'}>{r.appointedBy ?? 'ยังไม่ได้กรอก'}</dd>
                </div>
                <div>
                  <dt>ผู้ให้ความเห็นชอบหรืออนุมัติ</dt>
                  <dd className={r.approvedBy ? '' : 'todo'}>{r.approvedBy ?? 'ยังไม่ได้กรอก'}</dd>
                </div>
                <div>
                  <dt>ฐานทางกฎหมาย</dt>
                  <dd className="dim">{r.legalBasis ?? '—'}</dd>
                </div>
              </div>

              {r.steps.length > 0 && (
                <ol className="asteps">
                  {r.steps.map((s, i) => (
                    <li key={s}>
                      <span className="tnum">{i + 1}</span>
                      {s}
                    </li>
                  ))}
                </ol>
              )}

              {r.note && <p className="anote">{r.note}</p>}
            </div>
          ))}
        </div>

        <p className="hm-note">
          วิธีบันทึกให้ครบถ้วน: ขอไฟล์ พ.ร.บ. ระเบียบข้าราชการพลเรือน พ.ศ. 2551 ฉบับปัจจุบัน
          เปิดมาตรา 57 แล้วกรอกทีละอนุมาตราใน{' '}
          <code>server/src/domain/rules/appointmentAuthority.ts</code>{' '}
          พร้อมตรวจกับคำสั่งมอบอำนาจของกระทรวงด้วย เพราะบางระดับปลัดกระทรวงมอบอำนาจต่อได้
        </p>
    </>
  );
}
